import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";
import { NotificationVariant } from "Infrastructure/Api/Api";

type NotificationFormDataRaw = {
  durationType: "dismiss" | "period";
  duration: {
    from: { date: string; time: string };
    to?: { date?: string | null; time?: string | null } | null;
  };
};

const isEndDateProvidedForPeriod = ({
  durationType,
  duration,
}: NotificationFormDataRaw) => durationType !== "period" || !!duration.to?.date;

const isEndTimeProvidedForPeriod = ({
  durationType,
  duration,
}: NotificationFormDataRaw) => durationType !== "period" || !!duration.to?.time;

const isExpirationAfterScheduled = ({
  durationType,
  duration,
}: NotificationFormDataRaw) => {
  if (durationType !== "period" || !duration.to?.date || !duration.to?.time) {
    return true;
  }

  const scheduledDateTime = new Date(
    `${duration.from.date}T${duration.from.time}`,
  );
  const expirationDateTime = new Date(
    `${duration.to.date}T${duration.to.time}`,
  );

  return expirationDateTime > scheduledDateTime;
};

const isScheduledDateNotInPast = ({
  duration: { from },
}: NotificationFormDataRaw) => {
  if (!from.date) {
    return true;
  }

  const scheduledDate = new Date(from.date);
  const today = new Date();
  scheduledDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return scheduledDate >= today;
};

const isScheduledTimeNotInPast = ({
  duration: { from },
}: NotificationFormDataRaw) => {
  if (!from.date || !from.time) {
    return true;
  }

  const scheduledDate = new Date(from.date);
  const today = new Date();
  scheduledDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  // Only validate time if date is today
  if (scheduledDate.getTime() !== today.getTime()) {
    return true;
  }

  const scheduledDateTime = new Date(`${from.date}T${from.time}`);

  return scheduledDateTime > new Date();
};

type SchemaOptions = { skipDateTimeValidation?: boolean };

export const createNotificationSchema = (
  t: TFunction,
  { skipDateTimeValidation = false }: SchemaOptions,
) =>
  z
    .object({
      title: z
        .string()
        .min(1, t(Resources.Validation.Required))
        .max(200, t(Resources.Validation.MaxLength, { count: 200 })),
      content: z
        .string()
        .max(500, t(Resources.Validation.MaxLength, { count: 500 })),
      variant: z.nativeEnum(NotificationVariant),
      durationType: z.enum(["dismiss", "period"]),
      duration: z.object({
        from: z.object({
          date: z.string().min(1, t(Resources.Validation.Required)),
          time: z.string().min(1, t(Resources.Validation.Required)),
        }),
        to: z
          .object({ date: z.string().nullish(), time: z.string().nullish() })
          .nullish(),
      }),
      userTypes: z.array(z.string()).optional(),
      banks: z.array(z.string()).optional(),
      link: z
        .string()
        .url(t(Resources.Validation.InvalidUrl))
        .optional()
        .or(z.literal("")),
    })
    .refine(isEndDateProvidedForPeriod, {
      message: t(Resources.Validation.Required),
      path: ["duration", "to", "date"],
    })
    .refine(isEndTimeProvidedForPeriod, {
      message: t(Resources.Validation.Required),
      path: ["duration", "to", "time"],
    })
    .refine(isExpirationAfterScheduled, {
      message: t(Resources.Validation.ExpirationAfterScheduled),
      path: ["duration", "to", "date"],
    })
    .refine(
      (data) => skipDateTimeValidation || isScheduledDateNotInPast(data),
      {
        message: t(Resources.Validation.DateMustBeInFuture),
        path: ["duration", "from", "date"],
      },
    )
    .refine(
      (data) => skipDateTimeValidation || isScheduledTimeNotInPast(data),
      {
        message: t(Resources.Validation.TimeMustBeInFuture),
        path: ["duration", "from", "time"],
      },
    );

export type NotificationFormData = z.infer<
  ReturnType<typeof createNotificationSchema>
>;
